import React from 'react';
import { motion } from 'framer-motion';
import onlineAppointment from '../../assets/onlineAppointment.jpg';
import personalizedConsultation from '../../assets/personalizedConsultation.webp';
import treatmentPlan from '../../assets/treatmentPlan.avif';
import ongoingCare from '../../assets/ongoingCare.avif';
import { useNavigate } from 'react-router';

const steps = [
  {
    title: 'Schedule Your Visit',
    description:
      'Book online or by phone with ease. We offer flexible scheduling, including same-day and next-day appointments.',
    image: onlineAppointment,
    iconPath: 'M8 7V3m8 4V3m-9 8h10a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v7a2 2 0 002 2zm0 0v2m8-2v2'
  },
  {
    title: 'Personalized Consultation',
    description:
      'Meet one-on-one with your dentist. We listen to your goals, assess your oral health, and answer your questions.',
    image: personalizedConsultation,
    iconPath: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z'
  },
  {
    title: 'Treatment Plan + Options',
    description:
      'Receive a custom treatment plan with transparent pricing and multiple options—no pressure, just guidance.',
    image: treatmentPlan,
    iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2h-9z'
  },
  {
    title: 'Ongoing Care & Follow-Up',
    description:
      'Enjoy long-term support with preventive cleanings, timely reminders, and personalized dental care.',
    image: ongoingCare,
    iconPath: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
  }
];

const StepIcon = ({ path }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    className="h-6 w-6 absolute top-2 right-2 text-teal-500 opacity-50" 
    fill="none" 
    viewBox="0 0 24 24" 
    stroke="currentColor"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth={2} 
      d={path} 
    />
  </svg>
);

export function ServiceJourney() {
  const navigate = useNavigate();

  return (
    <section className="bg-gradient-to-br from-lime-50 to-teal-50 py-20 px-6">
      <div className="max-w-4xl mx-auto text-center mb-16">
        <motion.h2 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl font-bold text-gray-900 mb-4 bg-clip-text text-transparent bg-gradient-to-r from-lime-600 to-teal-600"
        >
          Your Smile Journey, Made Simple
        </motion.h2>
        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-gray-600 text-lg max-w-2xl mx-auto"
        >
          Whether it's your first visit or a smile transformation, we make the process easy, transparent, and tailored to your needs.
        </motion.p>
      </div>
      <div className="grid gap-8 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto">
        {steps.map((step, index) => (
          <motion.div 
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.2 }
            }}
            transition={{ 
              duration: 0.5, 
              delay: index * 0.2 
            }}
            className="relative bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-2xl border-t-4 border-teal-500"
          >
            <div className="relative h-48 w-full">
              <img
                src={step.image}
                alt={step.title}
                className="absolute inset-0 w-full h-full object-cover filter brightness-75 hover:brightness-100 transition-all duration-300"
                loading="lazy"
              />
              <StepIcon path={step.iconPath} />
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold text-teal-600 mb-2 flex items-center">
                <span className="mr-2 text-lime-500">{`0${index + 1}`}</span>
                {step.title}
              </h3>
              <p className="text-gray-600 text-sm">{step.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        className="mt-12 text-center"
      >
        <button
          onClick={() => navigate('/schedule-appointment')} 
          className="bg-gradient-to-r from-lime-500 to-teal-500 text-white px-8 py-3 rounded-full font-semibold hover:from-lime-600 hover:to-teal-600 transition-all duration-300 shadow-lg hover:shadow-xl">
          Start Your Smile Journey
        </button>
      </motion.div>
    </section>
  );
}

export default ServiceJourney;